import { useEffect, useLayoutEffect, useRef, useState } from "react";
import JourneyFinale from "./JourneyFinale";
import {
  queueMobileScrollSnap,
  setJourneyMobileScrollGate,
} from "@/journeyScrollGate";
import { MOBILE_ANCHOR_NAV_EVENT } from "@/mobileAnchorNav";
import { mobileStoryHashesBelowStory } from "@/mobileStoryNav";
import { getAssetUrl } from "@/lib/assetBaseUrl";

const STEPS = [
  {
    id: "step_01_personalized_plan",
    num: "01",
    title: "Personalized plan created",
    body: "A structured learning path is created around the student's goals, needs, and pace.",
    video: "https://asset.sparkl.me/pb/poc-folder/jU0jqD5QsSln1LPwsKrYgH3nMuJ3d8rjjcb3o4jy.mp4",
  },
  {
    id: "step_02_live_class",
    num: "02",
    title: "1-on-1 live classes",
    body: "The student learns with an expert teacher in a focused, personal live class.",
    video: "https://asset.sparkl.me/pb/poc-folder/cWy7zx8rFvDy4Su9Wj6on4DuKKSeKumXt51lE9tf.mp4",
  },
  {
    id: "step_03_test_capture",
    num: "03",
    title: "Tests capture performance",
    body: "Regular tests help identify what the student has understood and where support is needed.",
    videoPath: "pb/assets/video/x5lBvNLe56ryLPZOAkgKTFSIpCRFOVsAdiuuxsmG.mp4",
  },
  {
    id: "step_04_ai_engine",
    num: "04",
    title: "AI-Powered Intervention Insights",
    body: "Sparkl's AI Engine turns class and test data into focused next-step insights for teachers.",
    video: "https://asset.sparkl.me/pb/poc-folder/Y2jz5si885i7DhCRZOM5aHl73fWtzPtDW22N6b2g.mp4",
  },
  {
    id: "step_05_ai_tutor_support",
    num: "05",
    title: "AI tutor support is assigned",
    body: "The teacher assigns targeted AI tutor practice, revision, and concept reinforcement.",
    video: "https://asset.sparkl.me/pb/poc-folder/TH8M7p8EKyIz7MzKnWVKfgEAvNzMpmkDQmxM2zWP.mp4",
  },
];

/** Nodes are spaced evenly along the wave: t = i / (STEPS.length - 1). */
const STEP_COUNT = STEPS.length;
const PATH_DIV = STEP_COUNT - 1;

const ARROW_URL =
  "https://asset.sparkl.me/pb/poc-folder/rUcIoOcWh5TWQGl4lgx9Gd0jA2RrXkjwv3t8pBr2.png";

// Both DESKTOP and MOBILE geometry. The choreography logic is identical —
// only the geometry of the wave + circle differs.
const DESKTOP_GEOM = {
  VIEW_W: 1280,
  VIEW_H: 600,
  CURVE_D:
    "M 60 380 C 220 380, 300 200, 480 220 C 660 240, 720 80, 920 140 C 1120 200, 1160 70, 1240 80",
  CIRCLE_CX: 640,
  CIRCLE_CY: 300,
  CIRCLE_R: 250,
  // bottom half of circle: rightmost → bottom → leftmost (clockwise)
  CLOSING_ARC_D: "M 890 300 A 250 250 0 0 1 390 300",
  // wave morphs into TOP HALF of circle (left → top → right)
  buildCircleSamples(N) {
    const pts = [];
    const { CIRCLE_CX, CIRCLE_CY, CIRCLE_R } = this;
    for (let i = 0; i <= N; i += 1) {
      const t = i / N;
      const ang = Math.PI + Math.PI * t;
      pts.push([
        CIRCLE_CX + CIRCLE_R * Math.cos(ang),
        CIRCLE_CY + CIRCLE_R * Math.sin(ang),
      ]);
    }
    return pts;
  },
};

const MOBILE_GEOM = {
  VIEW_W: 640,
  VIEW_H: 900,
  // Vertical zigzag from top to bottom, oscillating widely around x=320.
  // Wider swings so the curve fills the mobile viewport horizontally.
  CURVE_D:
    "M 320 60 C 60 160, 580 240, 320 360 C 60 480, 580 560, 320 680 C 60 780, 540 840, 320 880",
  CIRCLE_CX: 320,
  CIRCLE_CY: 470,
  CIRCLE_R: 200,
  // Right half of circle: bottom → right → top
  // sweep-flag=0 (decreasing θ: 90°→0°→-90°) goes via x=520 (RIGHT side)
  // sweep-flag=1 would re-trace the left half that the wave already covers
  CLOSING_ARC_D: "M 320 670 A 200 200 0 0 0 320 270",
  // wave morphs into LEFT HALF of circle (top → left → bottom)
  buildCircleSamples(N) {
    const pts = [];
    const { CIRCLE_CX, CIRCLE_CY, CIRCLE_R } = this;
    for (let i = 0; i <= N; i += 1) {
      const t = i / N;
      // θ goes 0..π so the point traces top → left → bottom
      const θ = Math.PI * t;
      pts.push([
        CIRCLE_CX - CIRCLE_R * Math.sin(θ),
        CIRCLE_CY - CIRCLE_R * Math.cos(θ),
      ]);
    }
    return pts;
  },
};

// On mobile, node dots sit just outside the ring stroke (≥ active dot radius = 22).
const MOBILE_NODE_OUTWARD = 24;
// Number text is pushed further outward (radially) so it sits clearly outside
// the ring at every node position, not just the top.
const TEXT_RADIAL_OFFSET = 46;

// Number of polyline samples used for the wave→circle morph.
const N_SAMPLES = 80;

// Convert a list of [x, y] samples into an SVG polyline path string.
function ptsToPath(pts) {
  if (!pts || pts.length === 0) return "";
  let d = `M ${pts[0][0].toFixed(2)} ${pts[0][1].toFixed(2)}`;
  for (let i = 1; i < pts.length; i += 1) {
    d += ` L ${pts[i][0].toFixed(2)} ${pts[i][1].toFixed(2)}`;
  }
  return d;
}

// Linear interpolation point-by-point between two equal-length sample sets.
function morphPts(a, b, m) {
  const out = new Array(a.length);
  for (let i = 0; i < a.length; i += 1) {
    out[i] = [
      a[i][0] + (b[i][0] - a[i][0]) * m,
      a[i][1] + (b[i][1] - a[i][1]) * m,
    ];
  }
  return out;
}

// ─── Desktop choreography (HEAD values — unchanged from committed baseline) ───
/** Card expand + collapse — each phase +30% scroll vs 0.84 / 0.63 baselines. */
const ZOOM_IN = 0.84 * 1.3;
const HOLD = 0.84;
const ZOOM_OUT = 0.84 * 1.3;
const PER_ANCHOR = ZOOM_IN + HOLD + ZOOM_OUT; // ≈3.024

/** Desktop — travel uses units freed from shorter card phases. */
const TRAVEL_DESKTOP = 1.8;

// ─── Mobile-only choreography (tuned for touch; does NOT affect desktop) ────
/** Mobile beats — ~50% shorter so one swipe moves forward/back through each phase. */
const MOBILE_SCROLL_BEAT = 1.1;
const MOBILE_ZOOM_IN = MOBILE_SCROLL_BEAT;
const MOBILE_ZOOM_OUT = MOBILE_SCROLL_BEAT;
const MOBILE_HOLD = MOBILE_SCROLL_BEAT * 0.81;
/** Arrow travel between nodes (shortened with card beats; still smooth via arrow run). */
const TRAVEL_MOBILE = 3.2;

const MOBILE_PER_ANCHOR =
  MOBILE_ZOOM_IN + MOBILE_HOLD + MOBILE_ZOOM_OUT;

/** Desktop morph — unchanged from original. Mobile uses MOBILE_MORPH_UNITS for a slower transition. */
const MORPH_UNITS = 2;
const MOBILE_MORPH_UNITS = 6;
const FINALE_HOLD = 1.5;

/** Desktop timeline length (5 anchors + 4 travels + morph + finale). */
const TOTAL_UNITS =
  STEP_COUNT * PER_ANCHOR + PATH_DIV * TRAVEL_DESKTOP + MORPH_UNITS + FINALE_HOLD;

/** No lead-in — story starts when the section pins to the viewport top. */
const MOBILE_LEAD_IN_UNITS = 0;

/** Mobile timeline length (must match sum of mobile phases in timeline()). */
const MOBILE_TOTAL_UNITS =
  STEP_COUNT * MOBILE_PER_ANCHOR +
  PATH_DIV * TRAVEL_MOBILE +
  MOBILE_MORPH_UNITS +
  FINALE_HOLD;

function timelineDims(forMobileTimeline) {
  if (!forMobileTimeline) {
    return {
      zi: ZOOM_IN,
      hold: HOLD,
      zo: ZOOM_OUT,
      travel: TRAVEL_DESKTOP,
      per: PER_ANCHOR,
      total: TOTAL_UNITS,
    };
  }
  return {
    zi: MOBILE_ZOOM_IN,
    hold: MOBILE_HOLD,
    zo: MOBILE_ZOOM_OUT,
    travel: TRAVEL_MOBILE,
    per: MOBILE_PER_ANCHOR,
    total: MOBILE_TOTAL_UNITS,
  };
}

// Inside the MORPH phase, the first 40% lifts the wave into the oval's top
// half (arrow stays at the last step going DOWN). The last 60% draws the closing
// arc (bottom half of oval) — arrow walks DOWN, around, UP to step 1.
const MORPH_WAVE_FRAC = 0.4;

// Final on-screen size multiplier when the per-step card is fully zoomed.
// 1.8 × 0.8 = 1.44 (cards are 80% of the previous full-zoom size).
const FULL_SCALE = 1.44;

/** Desktop blend — HEAD value; smooth(t) easing in timeline handles the rest. */
const JOURNEY_SCROLL_BLEND_DESKTOP = 0.1;
/** Mobile card scale — smooth expand/collapse tied to scroll position. */
const MOBILE_SCALE_BLEND = 0.22;
/** Mobile arrow — light smoothing during path travel (scroll-driven). */
const MOBILE_ARROW_BLEND = 0.14;
/** Minimum p change to treat scroll as reverse on mobile. */
const MOBILE_SCROLL_BACK_EPS = 0.0008;
/** Mobile — tolerance when comparing animated values to step targets. */
const MOBILE_ANIM_EPS = 0.02;

/**
 * Map pinned scroll progress to scroll bands.
 * 0=idle (arrow at step 1), 1–5=card steps, 6=circle/finale, 7=exit to live.
 */
function mobileScrollStepFromP(p) {
  if (p < 0.125) return 0;
  if (p < 0.25) return 1;
  if (p < 0.375) return 2;
  if (p < 0.5) return 3;
  if (p < 0.625) return 4;
  if (p < 0.75) return 5;
  if (p < 0.875) return 6;
  return 7;
}

/** Step band upper bounds — one band per swipe (8 story bands + exit). */
const MOBILE_SCROLL_BAND_TOP = [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];
const MOBILE_SCROLL_BAND_EPS = 0.004;
/** Scroll this far above the pin line before resetting mobile story state. */
const MOBILE_JOURNEY_LEAVE_TOP_PX = 80;
const MOBILE_JOURNEY_BELOW_HASHES = mobileStoryHashesBelowStory("#journey");

/** Scroll band → committed display step (-1 idle .. 5 finale). */
function scrollStepToDisplayTarget(scrollStep) {
  if (scrollStep <= 0) return -1;
  return Math.min(scrollStep - 1, 5);
}

/** Cap scroll intent to one step ahead/behind committed progress. */
function boundedScrollIntent(displayStep, scrollStep) {
  if (scrollStep < 0) return displayStep;
  const target = scrollStepToDisplayTarget(scrollStep);
  if (displayStep < 0) {
    if (scrollStep < 1) return -1;
    return 0;
  }
  return Math.max(displayStep - 1, Math.min(displayStep + 1, target));
}

/**
 * Advance only on exact adjacent band — prevents reverse overshoot from
 * decrementing twice in one swipe (mirrors AI tutor step gating).
 * Settled displayStep N sits at scrollStep N+1; forward crosses N+2, reverse crosses N.
 */
function mobileJourneyShouldAdvanceStep(displayStep, scrollStep, idle) {
  if (!idle || displayStep < 0 || scrollStep < 0) return 0;
  if (scrollStep === displayStep + 2 && displayStep < 5) return 1;
  if (scrollStep === displayStep && displayStep > 0) return -1;
  return 0;
}

/**
 * Max normalised scroll p while the story is still in progress.
 * While animating: hold inside the current band.
 * When idle: allow one full band ahead (displayStep + 3 top) so the next
 * threshold is reachable; overshoot snap-back handles fast multi-band swipes.
 */
function mobileMaxScrollP(displayStep, animating) {
  if (displayStep < 0) {
    return MOBILE_SCROLL_BAND_TOP[2] - MOBILE_SCROLL_BAND_EPS;
  }
  if (displayStep >= 5) {
    return 1 - MOBILE_SCROLL_BAND_EPS;
  }
  const maxBandIdx = animating ? displayStep + 2 : displayStep + 3;
  return MOBILE_SCROLL_BAND_TOP[maxBandIdx] - MOBILE_SCROLL_BAND_EPS;
}

/**
 * Min normalised scroll p — mirrors max for reverse scrolling.
 * Until hero exit is allowed, lock to pin start (p=0) at step 0.
 * While animating: hold at the settled band. Idle: allow one band behind.
 */
function mobileMinScrollP(displayStep, animating, allowHeroExit) {
  if (displayStep <= 0) return allowHeroExit ? null : 0;
  if (animating) {
    return MOBILE_SCROLL_BAND_TOP[displayStep + 1] + MOBILE_SCROLL_BAND_EPS;
  }
  return MOBILE_SCROLL_BAND_TOP[displayStep] + MOBILE_SCROLL_BAND_EPS;
}

/** Visual targets for a committed mobile story step (0..5). */
function mobileStepTargets(step) {
  if (step < 0) {
    return { card: -1, scale: 0, arrow: 0, morph: 0, finale: 0 };
  }
  if (step <= 4) {
    return {
      card: step,
      scale: 1,
      arrow: step / PATH_DIV,
      morph: 0,
      finale: 0,
    };
  }
  return { card: -1, scale: 0, arrow: 1, morph: 1, finale: 1 };
}

/** True when the mobile animation has fully reached the given step. */
function isMobileAnimIdle(arrow, card, scale, morph, finale, step) {
  const t = mobileStepTargets(step);
  if (step < 0) {
    return (
      card === -1 &&
      scale < MOBILE_ANIM_EPS &&
      Math.abs(arrow) < MOBILE_ANIM_EPS &&
      morph < MOBILE_ANIM_EPS &&
      finale < MOBILE_ANIM_EPS
    );
  }
  return (
    card === t.card &&
    Math.abs(scale - t.scale) < MOBILE_ANIM_EPS &&
    Math.abs(arrow - t.arrow) < MOBILE_ANIM_EPS &&
    Math.abs(morph - t.morph) < MOBILE_ANIM_EPS &&
    Math.abs(finale - t.finale) < MOBILE_ANIM_EPS
  );
}

/** Snap mobile state to a step (anchor jump / section leave). */
function snapMobileStateToStep(step) {
  const t = mobileStepTargets(step);
  return {
    arrow: t.arrow,
    card: t.card,
    scale: t.scale,
    morph: t.morph,
    finale: t.finale,
  };
}

/** Pin-line scroll Y for a mobile story band index (0 = hero exit / idle). */
function mobileJourneySnapScrollY(sectionEl, bandIdx, scrollRange) {
  const pinStartY = sectionEl.offsetTop;
  const bandP =
    bandIdx <= 0
      ? 0
      : MOBILE_SCROLL_BAND_TOP[bandIdx] + MOBILE_SCROLL_BAND_EPS;
  return pinStartY + bandP * scrollRange;
}
/** Cubic smoothstep — used for desktop arrow travel (HEAD behaviour). */
function smooth(t) {
  return t * t * (3 - 2 * t);
}

function clamp01(v) {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

/** Shared ease-in-out for journey + live scroll stories (arrow travel, zoom, morph). */
function easeInOutCubic(t) {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}

// Returns the full timeline state for normalised scroll p in [0,1]:
//   arrow: 0..1 along the wave (only meaningful while morph === 0)
//   idx:   active step (0..STEP_COUNT-1) or -1 between phases
//   scale: card scale 0..1 (sine ease-in-out zoom in / out — symmetric expand ↔ compress)
//   morph: 0..1 progress of curve→circle transformation
//   finale: 0..1 progress of finale-hold (video plays at finale === 1)
function timeline(p, useMobileTimeline) {
  const d = timelineDims(useMobileTimeline);
  const rawU = clamp01(p) * d.total;
  const leadIn = useMobileTimeline ? MOBILE_LEAD_IN_UNITS : 0;
  if (useMobileTimeline && rawU < leadIn) {
    return {
      arrow: 0,
      idx: -1,
      scale: 0,
      morph: 0,
      finale: 0,
    };
  }
  const u = rawU - leadIn;
  let cursor = 0;
  for (let i = 0; i < STEP_COUNT; i += 1) {
    const aEnd = cursor + d.per;
    if (u <= aEnd) {
      const local = Math.max(0, u - cursor);
      let scale;
      if (local < d.zi) {
        // Mobile: smooth ease-in-out expand. Desktop: linear (HEAD — unchanged).
        scale = useMobileTimeline ? easeInOutCubic(local / d.zi) : local / d.zi;
      } else if (local < d.zi + d.hold) {
        scale = 1;
      } else {
        const t = (local - d.zi - d.hold) / d.zo;
        scale = 1 - (useMobileTimeline ? easeInOutCubic(t) : t);
      }
      return {
        arrow: i / PATH_DIV,
        idx: i,
        scale: clamp01(scale),
        morph: 0,
        finale: 0,
      };
    }
    cursor = aEnd;
    if (i < PATH_DIV) {
      const tEnd = cursor + d.travel;
      if (u <= tEnd) {
        // Mobile + desktop: smooth S-curve arrow travel between nodes.
        const raw = (u - cursor) / d.travel;
        const t = smooth(raw);
        return {
          arrow: (i + t) / PATH_DIV,
          idx: -1,
          scale: 0,
          morph: 0,
          finale: 0,
        };
      }
      cursor = tEnd;
    }
  }
  // After all anchors finish: morph the curve into a closed circle while
  // the arrow walks (with the curve) toward step 1's final circle position.
  // Mobile gets a longer morph window (MOBILE_MORPH_UNITS) for a visibly slow transition.
  const activeMorphUnits = useMobileTimeline ? MOBILE_MORPH_UNITS : MORPH_UNITS;
  const mEnd = cursor + activeMorphUnits;
  if (u <= mEnd) {
    const mLin = (u - cursor) / activeMorphUnits;
    return {
      arrow: 1,
      idx: -1,
      scale: 0,
      // Mobile: cubic easing so morph is visibly slow. Desktop: linear (HEAD).
    morph: useMobileTimeline ? easeInOutCubic(mLin) : clamp01(mLin),
      finale: 0,
    };
  }
  cursor = mEnd;
  // Finale hold: circle fully formed, video plays inside, capsules visible.
  const fLin = (u - cursor) / FINALE_HOLD;
  return {
    arrow: 1,
    idx: -1,
    scale: 0,
    morph: 1,
    // Mobile: ease finale reveal. Desktop: linear (HEAD).
    finale: useMobileTimeline ? easeInOutCubic(fLin) : clamp01(fLin),
  };
}

/** Mobile-only: active travel leg between two path nodes (arrow mid-route). */
function mobileTravelLeg(p) {
  const d = timelineDims(true);
  const rawU = clamp01(p) * d.total;
  if (rawU < MOBILE_LEAD_IN_UNITS) return null;
  const u = rawU - MOBILE_LEAD_IN_UNITS;
  let cursor = 0;
  for (let i = 0; i < PATH_DIV; i += 1) {
    cursor += d.per;
    const tEnd = cursor + d.travel;
    if (u <= tEnd) {
      const local = u - cursor;
      if (local <= 0.001) return null;
      const raw = local / d.travel;
      const t = smooth(raw);
      return {
        fromNode: i,
        toNode: i + 1,
        frac: raw,
        arrow: (i + t) / PATH_DIV,
        targetArrow: (i + 1) / PATH_DIV,
        legKey: `${i}-${i + 1}`,
      };
    }
    cursor = tEnd;
  }
  return null;
}

/** Positions + trail/active styling for journey dots (shared by scroll + layout). */
function paintJourneyNodes({
  tl,
  m,
  mWave,
  waveNodes,
  circleTopNodes,
  geom,
  nodeRefs,
  numRefs,
  stepCount,
  pathDiv,
  storyStep = -1,
  arrowTraveling = false,
}) {
  const idx = tl.idx;
  const mobile = geom.VIEW_W === MOBILE_GEOM.VIEW_W;
  const tArrow = m > (mobile ? 0.06 : 0) ? 1 : tl.arrow;
  /** True entry idle only — not step 1 committed (storyStep 0) with arrow at node 1 */
  const introIdle =
    mobile && storyStep < 0 && idx === -1 && m === 0 && tArrow <= 1e-6;
  const filterActive = "drop-shadow(0 10px 22px rgba(250,172,0,0.30))";
  const filterTrail = "drop-shadow(0 6px 14px rgba(31,41,55,0.10))";

  const rTrail = mobile ? 14 : 10;
  const rActive = mobile ? 22 : 18;
  const transitionDots = mobile
    ? "r 0.55s cubic-bezier(0.33, 1, 0.36, 1), fill 0.55s cubic-bezier(0.33, 1, 0.36, 1), stroke-width 0.45s cubic-bezier(0.33, 1, 0.36, 1)"
    : "r 0.45s cubic-bezier(0.22,1,0.36,1), fill 0.5s ease";

  for (let i = 0; i < stepCount; i += 1) {
    const w = waveNodes[i];
    const oPath = circleTopNodes[i];
    if (!w || !oPath || !nodeRefs.current[i]) continue;
    let x = w[0] + (oPath[0] - w[0]) * mWave;
    let y = w[1] + (oPath[1] - w[1]) * mWave;
    if (mWave > 0) {
      const dx = oPath[0] - geom.CIRCLE_CX;
      const dy = oPath[1] - geom.CIRCLE_CY;
      const len = Math.hypot(dx, dy) || 1;
      if (mobile) {
        const push = MOBILE_NODE_OUTWARD * mWave;
        x += (dx / len) * push;
        y += (dy / len) * push;
        if (numRefs.current[i]) {
          const tx = (dx / len) * TEXT_RADIAL_OFFSET * mWave;
          const ty = (dy / len) * TEXT_RADIAL_OFFSET * mWave + (-36) * (1 - mWave);
          numRefs.current[i].setAttribute("x", tx.toFixed(1));
          numRefs.current[i].setAttribute("y", ty.toFixed(1));
        }
      } else {
        // Desktop: push number text radially outward so equatorial nodes
        // (01 at 9-o'clock, 05 at 3-o'clock) don't overlap the circle stroke.
        if (numRefs.current[i]) {
          const DESKTOP_TEXT_RADIAL = 32;
          const tx = (dx / len) * DESKTOP_TEXT_RADIAL * mWave;
          const ty = (dy / len) * DESKTOP_TEXT_RADIAL * mWave + (-26) * (1 - mWave);
          numRefs.current[i].setAttribute("x", tx.toFixed(1));
          numRefs.current[i].setAttribute("y", ty.toFixed(1));
        }
      }
    }
    nodeRefs.current[i].setAttribute("transform", `translate(${x}, ${y})`);

    const tNode = i / pathDiv;
    const isActive = idx === i;
    const isCompleted =
      !introIdle &&
      !isActive &&
      (i < idx || tArrow + 1e-10 >= tNode);

    /** Mobile path travel: glide dot sizes with arrow (no hard on/off active jump). */
    let travelHalo = 0;
    if (mobile && m === 0 && !introIdle && (idx === -1 || arrowTraveling)) {
      const frac = tArrow * pathDiv;
      travelHalo = clamp01(1 - Math.abs(frac - i)) ** 0.82;
    }

    const circle = nodeRefs.current[i].querySelector("circle");
    if (circle) {
      let r;
      if (travelHalo > 0) {
        r = rTrail + (rActive - rTrail) * travelHalo;
      } else {
        r = isActive ? rActive : rTrail;
      }
      circle.setAttribute("r", String(r));
      circle.setAttribute(
        "fill",
        isActive
          ? "url(#sp-active-grad)"
          : isCompleted
            ? "#97D8C4"
            : "#FFFFFF"
      );
      circle.setAttribute("stroke", "#FFFFFF");
      const strokeW =
        isActive ? 3 : travelHalo > 0 ? 2 + travelHalo : 2;
      circle.setAttribute("stroke-width", String(strokeW));
      circle.setAttribute(
        "style",
        `filter: ${isActive ? filterActive : filterTrail}; transition: ${transitionDots}`
      );
    }
    const numEl = numRefs.current[i];
    if (numEl) {
      numEl.setAttribute(
        "fill",
        isActive ? "#1E2430" : "rgba(30,36,48,0.55)"
      );
    }
  }
}

export default function JourneySection() {
  const sectionRef = useRef(null);
  const pathRef = useRef(null);
  const arrowRef = useRef(null);
  const activeStrokeRef = useRef(null);
  const closingArcRef = useRef(null);
  const panelRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRefs = useRef([]);
  const nodeRefs = useRef([]);
  const numRefs = useRef([]);

  const mobArrowRef = useRef(0);
  const mobActiveCardRef = useRef(-1);
  const mobCardScaleRef = useRef(0);
  const mobMorphRef = useRef(0);
  const mobFinaleRef = useRef(0);
  /** Committed mobile story step (-1 idle, 0..4 cards, 5 finale). */
  const mobDisplayStepRef = useRef(-1);
  /** True only when step 0 first card is done and user may scroll up to hero. */
  const mobAllowHeroExitRef = useRef(false);
  /**
   * User scrolled past journey (down) or jumped to a section below it —
   * reverse re-entry should start at the finale (arrow at path end).
   */
  const mobEnteredFromBelowRef = useRef(false);
  /** Blocks a second step change until the finger lifts (one swipe = one step). */
  const mobStepLockedThisGestureRef = useRef(false);
  const lastTimeRef = useRef(performance.now());

  // Mobile gets the SAME scroll-pinned choreography but with a vertical
  // wave + LEFT-half closing into a circle on the RIGHT.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 767
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    if (!isMobile) return undefined;
    const onAnchorNav = (e) => {
      const hash = e.detail?.hash;
      if (hash === "#journey") {
        mobEnteredFromBelowRef.current = false;
        mobDisplayStepRef.current = -1;
        const s = snapMobileStateToStep(-1);
        mobArrowRef.current = s.arrow;
        mobActiveCardRef.current = s.card;
        mobCardScaleRef.current = s.scale;
        mobMorphRef.current = s.morph;
        mobFinaleRef.current = s.finale;
        mobAllowHeroExitRef.current = false;
        setJourneyMobileScrollGate(false);
        return;
      }
      if (hash && MOBILE_JOURNEY_BELOW_HASHES.has(hash)) {
        mobEnteredFromBelowRef.current = true;
        mobDisplayStepRef.current = -1;
        const s = snapMobileStateToStep(-1);
        mobArrowRef.current = s.arrow;
        mobActiveCardRef.current = s.card;
        mobCardScaleRef.current = s.scale;
        mobMorphRef.current = s.morph;
        mobFinaleRef.current = s.finale;
        mobAllowHeroExitRef.current = false;
      }
    };
    window.addEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
    return () => window.removeEventListener(MOBILE_ANCHOR_NAV_EVENT, onAnchorNav);
  }, [isMobile]);
  useEffect(() => {
    if (!isMobile) return undefined;
    const onTouchStart = () => {
      mobStepLockedThisGestureRef.current = false;
    };
    const onTouchEnd = () => {
      mobStepLockedThisGestureRef.current = false;
    };
    const touchOpts = { passive: true, capture: true };
    document.addEventListener("touchstart", onTouchStart, touchOpts);
    document.addEventListener("touchend", onTouchEnd, touchOpts);
    document.addEventListener("touchcancel", onTouchEnd, touchOpts);
    return () => {
      document.removeEventListener("touchstart", onTouchStart, touchOpts);
      document.removeEventListener("touchend", onTouchEnd, touchOpts);
      document.removeEventListener("touchcancel", onTouchEnd, touchOpts);
    };
  }, [isMobile]);
  const geom = isMobile ? MOBILE_GEOM : DESKTOP_GEOM;

  const [active, setActive] = useState(-1);
  const [videoActive, setVideoActive] = useState(-1);
  const [waveSamples, setWaveSamples] = useState(null);
  const [circleTopSamples, setCircleTopSamples] = useState(() =>
    geom.buildCircleSamples(N_SAMPLES)
  );
  const [finaleProgress, setFinaleProgress] = useState(0);

  const finaleRef = useRef(null);
  const svgRef = useRef(null);

  // Resample the wave + circle whenever geom (i.e. isMobile) changes.
  useEffect(() => {
    const ns = "http://www.w3.org/2000/svg";
    const tmp = document.createElementNS(ns, "path");
    tmp.setAttribute("d", geom.CURVE_D);
    const len = tmp.getTotalLength();
    const samples = [];
    for (let i = 0; i <= N_SAMPLES; i += 1) {
      const t = i / N_SAMPLES;
      const pt = tmp.getPointAtLength(len * t);
      samples.push([pt.x, pt.y]);
    }
    setWaveSamples(samples);
    setCircleTopSamples(geom.buildCircleSamples(N_SAMPLES));
  }, [geom]);

  // STEP_COUNT node positions on the wave and on the final circle (top half
  // for desktop, left half for mobile), evenly spaced along the sampled path.
  const waveNodes = waveSamples
    ? Array.from({ length: STEP_COUNT }, (_, i) => {
        const t = i / PATH_DIV;
        const idx = Math.round(t * N_SAMPLES);
        return waveSamples[idx];
      })
    : [];
  const circleTopNodes = Array.from({ length: STEP_COUNT }, (_, i) => {
    const idx = Math.round((i / PATH_DIV) * N_SAMPLES);
    return circleTopSamples[idx];
  });

  // Scroll-linked driver
  useEffect(() => {
    const sec = sectionRef.current;
    const path = pathRef.current;
    const stroke = activeStrokeRef.current;
    const arc = closingArcRef.current;
    const canvas = canvasRef.current;
    if (!sec || !path || !arc || !canvas || !waveSamples) return undefined;

    // The closing arc length is constant once it's mounted; we drive it via
    // stroke-dashoffset to give the appearance of "drawing itself".
    const arcLen = arc.getTotalLength();
    arc.style.strokeDasharray = String(arcLen);
    arc.style.strokeDashoffset = String(arcLen);

    // smoothP: eased scroll progress — tighter blend on MOBILE_GEOM layouts (+20% vs desktop).
    let smoothP = 0;
    let rafId = 0;
    let lastActive = -2;
    let lastVideo = -2;
    let lastFinale = -1;
    let lastMorphWave = -1;

    /**
     * scaleSmooth: secondary lerp on the card scale value.
     * The timeline produces `s` (0→1) based on scroll position, but applying it
     * directly each frame makes the zoom invisible on fast swipes.
     * scaleSmooth trails `s` at a controlled rate so expand/collapse always takes
     * a perceptible number of frames regardless of scroll speed.
     * lastNodePt caches the active node's SVG coords so the card stays positioned
     * correctly while scaleSmooth is still decaying after the travel phase starts.
     */
    let scaleSmooth = 0;
    let lastNodePt = null;

    /**
     * arrowSmooth: mobile-only secondary lerp on the arrow's path progress (0→1).
     * Gives the arrow a gentle trailing "glide" so it never snaps between positions
     * on fast swipes. Desktop uses tl.arrow directly (HEAD behaviour).
     */
    let arrowSmooth = 0;

    const update = (p, dt = 0) => {

      const mobileTl = geom.VIEW_W === MOBILE_GEOM.VIEW_W;
      const secRect = mobileTl ? sec.getBoundingClientRect() : null;
      const journeyPinned = !mobileTl || (secRect && secRect.top <= 0);
      const journeyInView =
        mobileTl &&
        secRect &&
        secRect.bottom > 0 &&
        secRect.top < window.innerHeight;
      const leftJourneyUpward =
        mobileTl && secRect && secRect.top > MOBILE_JOURNEY_LEAVE_TOP_PX;

      let idx;
      let s;
      let m;
      let f;
      let arrowDraw;
      let deskTimeline = null;
      let mobilePaintStoryStep = -1;
      let mobilePaintArrowTraveling = false;

      if (mobileTl) {
        // --- Mobile View State Machine ---
        // Scroll maps to a desired step, but we only advance the committed
        // display step by ±1 after each full animation cycle — so rapid swipes
        // cannot skip card expand/collapse or rush the arrow along the path.
        let scrollStep = -1;
        const scrollRange = sec.offsetHeight - window.innerHeight;
        const rawP =
          secRect && scrollRange > 0
            ? Math.max(0, Math.min(1, -secRect.top / scrollRange))
            : 0;
        if (
          secRect &&
          journeyInView &&
          (secRect.top <= 0 || mobDisplayStepRef.current >= 0)
        ) {
          scrollStep = mobileScrollStepFromP(rawP);
        }

        // Speed coefficients (amount per second) — tuned to finish before scroll band completes
        const cardSpeed = 3.15; // ~0.32s expand/collapse
        const arrowSpeed = 0.88; // ~0.28s between nodes
        const morphSpeed = 1.85; // ~0.54s morph
        const finaleSpeed = 2.1; // ~0.48s finale reveal
        
        let mobArrow = mobArrowRef.current;
        let mobActiveCard = mobActiveCardRef.current;
        let mobCardScale = mobCardScaleRef.current;
        let mobMorph = mobMorphRef.current;
        let mobFinale = mobFinaleRef.current;
        let displayStep = mobDisplayStepRef.current;
        const prevDisplayStep = displayStep;
        let queueFinaleEntrySnap = false;
        let queueHeroExitSnap = false;

        const idleAtStep0 =
          displayStep === 0 &&
          isMobileAnimIdle(
            mobArrow,
            mobActiveCard,
            mobCardScale,
            mobMorph,
            mobFinale,
            0,
          );
        const mayResetUpward =
          leftJourneyUpward &&
          (displayStep < 0 ||
            (displayStep === 0 && idleAtStep0 && scrollStep <= 0));
        const mayResetPast =
          secRect &&
          secRect.bottom <= 0 &&
          (displayStep < 0 ||
            isMobileAnimIdle(
              mobArrow,
              mobActiveCard,
              mobCardScale,
              mobMorph,
              mobFinale,
              displayStep >= 0 ? displayStep : 0,
            ));
        const cardStillVisible =
          mobCardScale > MOBILE_ANIM_EPS && mobActiveCard >= 0;
        const reverseToIdle =
          displayStep === 0 && scrollStep <= 0 && cardStillVisible;

        if (mayResetUpward) {
          mobEnteredFromBelowRef.current = false;
          displayStep = -1;
          if (!cardStillVisible) {
            ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
              snapMobileStateToStep(-1));
          }
        } else if (mayResetPast) {
          mobEnteredFromBelowRef.current = true;
          displayStep = -1;
          if (!cardStillVisible) {
            ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
              snapMobileStateToStep(-1));
          }
        } else if (displayStep < 0 && secRect && secRect.top <= 0) {
          if (mobEnteredFromBelowRef.current) {
            displayStep = 5;
            ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
              snapMobileStateToStep(5));
            mobEnteredFromBelowRef.current = false;
            queueFinaleEntrySnap = true;
          } else if (scrollStep >= 1) {
            displayStep = Math.min(scrollStep - 1, 5);
            if (scrollStep === 1) {
              displayStep = 0;
              mobArrow = 0;
              mobActiveCard = -1;
              mobCardScale = 0;
              mobMorph = 0;
              mobFinale = 0;
            } else {
              ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
                snapMobileStateToStep(displayStep));
            }
          } else if (!cardStillVisible) {
            ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
              snapMobileStateToStep(-1));
          }
        } else if (displayStep < 0) {
          // Approaching journey — wait until section pins to viewport top.
        } else if (dt > 0) {
          const idle = isMobileAnimIdle(
            mobArrow,
            mobActiveCard,
            mobCardScale,
            mobMorph,
            mobFinale,
            displayStep,
          );
          const stepDelta = mobileJourneyShouldAdvanceStep(
            displayStep,
            scrollStep,
            idle,
          );
          const canChangeStep = !mobStepLockedThisGestureRef.current;
          if (!queueFinaleEntrySnap && canChangeStep) {
            if (stepDelta === 1) {
              displayStep += 1;
              mobStepLockedThisGestureRef.current = true;
            } else if (stepDelta === -1) {
              displayStep -= 1;
              mobStepLockedThisGestureRef.current = true;
            } else if (
              idle &&
              displayStep === 0 &&
              scrollStep === 0 &&
              !cardStillVisible
            ) {
              displayStep = -1;
              queueHeroExitSnap = true;
              mobStepLockedThisGestureRef.current = true;
            } else if (displayStep === 5 && scrollStep === 5) {
              // Reverse from circle morph/finale — don't wait for step 5 idle.
              displayStep -= 1;
              mobStepLockedThisGestureRef.current = true;
            }
          }
        }

        mobDisplayStepRef.current = displayStep;
        const targetState = reverseToIdle ? -1 : displayStep;
        const scrollIntent = boundedScrollIntent(displayStep, scrollStep);

        const targetCard = targetState >= 0 && targetState <= 4 ? targetState : -1;
        const targetArrow =
          targetState >= 0 && targetState <= 4
            ? targetState / PATH_DIV
            : targetState === 5
              ? 1.0
              : 0;
        const arrowAtTarget = Math.abs(mobArrow - targetArrow) < MOBILE_ANIM_EPS;
        const morphUnwind =
          targetState < 5 &&
          (mobFinale > MOBILE_ANIM_EPS || mobMorph > MOBILE_ANIM_EPS);
        const cardVisible =
          mobCardScale > MOBILE_ANIM_EPS && mobActiveCard >= 0;
        const needsCollapse =
          cardVisible &&
          (targetState < 0 || mobActiveCard !== targetCard);
        const blockArrowAdvance =
          cardVisible && mobActiveCard !== targetCard;
        const canExpandCard =
          !morphUnwind &&
          arrowAtTarget &&
          targetCard >= 0 &&
          mobCardScale < 1 - MOBILE_ANIM_EPS &&
          (mobActiveCard < 0 || mobActiveCard === targetCard);
        
        if (dt > 0) {
          if (needsCollapse) {
            mobCardScale = Math.max(0, mobCardScale - cardSpeed * dt);
            if (mobCardScale < MOBILE_ANIM_EPS) {
              mobCardScale = 0;
              mobActiveCard = -1;
            }
          }

          if (!morphUnwind && !arrowAtTarget) {
            if (mobArrow > targetArrow + MOBILE_ANIM_EPS) {
              mobArrow = Math.max(targetArrow, mobArrow - arrowSpeed * dt);
            } else if (
              mobArrow < targetArrow - MOBILE_ANIM_EPS &&
              !blockArrowAdvance
            ) {
              mobArrow = Math.min(targetArrow, mobArrow + arrowSpeed * dt);
            }
          }

          if (targetState === -1) {
            mobMorph = Math.max(0, mobMorph - morphSpeed * dt);
            mobFinale = Math.max(0, mobFinale - finaleSpeed * dt);
          } else if (morphUnwind) {
            if (mobFinale > MOBILE_ANIM_EPS) {
              mobFinale = Math.max(0, mobFinale - finaleSpeed * dt);
            } else if (mobMorph > MOBILE_ANIM_EPS) {
              mobMorph = Math.max(0, mobMorph - morphSpeed * dt);
            }
          } else if (canExpandCard) {
            mobActiveCard = targetCard;
            mobCardScale = Math.min(1, mobCardScale + cardSpeed * dt);
          } else if (targetState === 5) {
            if (
              scrollIntent < 5 &&
              (mobMorph > MOBILE_ANIM_EPS || mobFinale > MOBILE_ANIM_EPS)
            ) {
              if (mobFinale > MOBILE_ANIM_EPS) {
                mobFinale = Math.max(0, mobFinale - finaleSpeed * dt);
              } else if (mobMorph > MOBILE_ANIM_EPS) {
                mobMorph = Math.max(0, mobMorph - morphSpeed * dt);
              }
            } else if (mobMorph < 1 - MOBILE_ANIM_EPS) {
              mobMorph = Math.min(1, mobMorph + morphSpeed * dt);
            } else if (mobFinale < 1 - MOBILE_ANIM_EPS) {
              mobFinale = Math.min(1, mobFinale + finaleSpeed * dt);
            }
          }
        } else {
          // Resize / init — snap to committed step when section is pinned
          if (journeyPinned && displayStep >= 0) {
            ({ arrow: mobArrow, card: mobActiveCard, scale: mobCardScale, morph: mobMorph, finale: mobFinale } =
              snapMobileStateToStep(displayStep));
          }
        }

        if (reverseToIdle && mobCardScale < MOBILE_ANIM_EPS && mobActiveCard < 0) {
          displayStep = -1;
          mobDisplayStepRef.current = -1;
          if (prevDisplayStep === 0) {
            queueHeroExitSnap = true;
          }
        }

        // Save back to refs
        mobArrowRef.current = mobArrow;
        mobActiveCardRef.current = mobActiveCard;
        mobCardScaleRef.current = mobCardScale;
        mobMorphRef.current = mobMorph;
        mobFinaleRef.current = mobFinale;

        idx = mobActiveCard;
        s = mobCardScale;
        m = mobMorph;
        f = mobFinale;
        arrowDraw = mobArrow;
        scaleSmooth = mobCardScale;
        mobilePaintStoryStep = displayStep;
        mobilePaintArrowTraveling =
          mobCardScale < MOBILE_ANIM_EPS &&
          mobMorph < MOBILE_ANIM_EPS &&
          !arrowAtTarget;

        const animating =
          reverseToIdle ||
          (displayStep >= 0 &&
            !isMobileAnimIdle(
              mobArrow,
              mobActiveCard,
              mobCardScale,
              mobMorph,
              mobFinale,
              displayStep,
            ));
        const storyComplete =
          displayStep === 5 &&
          !animating &&
          isMobileAnimIdle(
            mobArrow,
            mobActiveCard,
            mobCardScale,
            mobMorph,
            mobFinale,
            5,
          );
        const idleAtEntry =
          displayStep < 0 &&
          isMobileAnimIdle(
            mobArrow,
            mobActiveCard,
            mobCardScale,
            mobMorph,
            mobFinale,
            -1,
          );
        const allowHeroExit = idleAtEntry && scrollStep <= 0;
        const allowForwardExit = storyComplete && scrollStep >= 7;
        const allowUpwardLeave = allowHeroExit;
        mobAllowHeroExitRef.current = allowHeroExit;

        const pinStartY = sec.offsetTop;
        const sectionEndY = pinStartY + scrollRange;
        const journeyPinnedActive =
          journeyInView && secRect && secRect.top <= 0;

        if (journeyPinnedActive && !allowForwardExit && !allowUpwardLeave) {
          let maxP;
          let minP;
          if (reverseToIdle) {
            maxP = MOBILE_SCROLL_BAND_TOP[1] - MOBILE_SCROLL_BAND_EPS;
            minP = 0;
          } else {
            maxP = mobileMaxScrollP(displayStep, animating);
            if (storyComplete && scrollStep < 7) {
              maxP = 1 - MOBILE_SCROLL_BAND_EPS;
            }
            minP = mobileMinScrollP(
              displayStep,
              animating,
              allowHeroExit,
            );
          }
          const minScrollY =
            minP != null ? pinStartY + minP * scrollRange : pinStartY;
          const maxScrollY = Math.min(
            pinStartY + maxP * scrollRange,
            sectionEndY,
          );
          setJourneyMobileScrollGate(
            true,
            minScrollY,
            maxScrollY,
            pinStartY,
            sectionEndY,
          );
          const settledAtStep = displayStep >= 0 && !animating;
          if (queueFinaleEntrySnap) {
            queueMobileScrollSnap(
              mobileJourneySnapScrollY(sec, 6, scrollRange),
              "journey",
            );
          } else if (queueHeroExitSnap) {
            queueMobileScrollSnap(
              mobileJourneySnapScrollY(sec, 0, scrollRange),
              "journey",
            );
          } else if (
            displayStep !== prevDisplayStep &&
            displayStep >= 0
          ) {
            const bandIdx = displayStep <= 4 ? displayStep + 1 : 6;
            queueMobileScrollSnap(
              mobileJourneySnapScrollY(sec, bandIdx, scrollRange),
              "journey",
            );
          } else if (
            settledAtStep &&
            scrollStep > displayStep + 1 &&
            displayStep < 5
          ) {
            // Pull forward overshoot back to the settled band (not the trigger band).
            queueMobileScrollSnap(
              mobileJourneySnapScrollY(sec, displayStep + 1, scrollRange),
              "journey",
            );
          } else if (
            settledAtStep &&
            scrollStep < displayStep &&
            displayStep > 0
          ) {
            // Deep reverse overshoot only — scrollStep === displayStep is the valid trigger band.
            queueMobileScrollSnap(
              mobileJourneySnapScrollY(sec, displayStep + 1, scrollRange),
              "journey",
            );
          }
        } else {
          setJourneyMobileScrollGate(false);
        }
      } else {
        deskTimeline = timeline(p, false);
        idx = deskTimeline.idx;
        s = deskTimeline.scale;
        m = deskTimeline.morph;
        f = deskTimeline.finale;

        arrowSmooth += (deskTimeline.arrow - arrowSmooth) * 0.12;
        arrowDraw = arrowSmooth;
        scaleSmooth = s;
      }

      const mWave = m === 0 ? 0 : Math.min(1, m / MORPH_WAVE_FRAC);
      const mArc =
        m === 0 ? 0 : Math.max(0, Math.min(1, (m - MORPH_WAVE_FRAC) / (1 - MORPH_WAVE_FRAC)));

      // ---------- Wave path morph (diagonal -> circle top half) ----------
      const morphSteps = geom.VIEW_W === MOBILE_GEOM.VIEW_W ? 96 : 200;
      const mq = Math.round(mWave * morphSteps) / morphSteps;
      if (mq !== lastMorphWave) {
        lastMorphWave = mq;
        const morphed = mq === 0
          ? waveSamples
          : mq === 1
          ? circleTopSamples
          : morphPts(waveSamples, circleTopSamples, mq);
        const d = ptsToPath(morphed);
        path.setAttribute("d", d);
        if (stroke) stroke.setAttribute("d", d);
        const len = path.getTotalLength();
        if (stroke) stroke.setAttribute("stroke-dasharray", String(len));
      }

      const waveLen = path.getTotalLength();

      const morphArrowDone = mobileTl ? m > 0.06 : m > 0;

      // ---------- Active gradient stroke — always matches arrow tip on mobile ----------
      if (stroke) {
        if (mobileTl) stroke.style.transition = "none";
        const drawn = morphArrowDone ? 1 : arrowDraw;
        stroke.style.strokeDashoffset = String(waveLen * (1 - drawn));
      }

      // ---------- Closing arc grows during the second half of MORPH ----------
      arc.style.strokeDashoffset = String(arcLen * (1 - mArc));

      let ax;
      let ay;
      let angle;
      if (mArc > 0) {
        const t = mArc;
        const pt = arc.getPointAtLength(arcLen * t);
        const eps = Math.min(2, arcLen * 0.001);
        const ahead = arc.getPointAtLength(Math.min(arcLen, arcLen * t + eps));
        const behind = arc.getPointAtLength(Math.max(0, arcLen * t - eps));
        ax = pt.x;
        ay = pt.y;
        angle = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI;
      } else {
        const arrowT = morphArrowDone ? 1 : arrowDraw;
        const pt = path.getPointAtLength(waveLen * arrowT);
        const eps = Math.min(2, waveLen * 0.001);
        const ahead = path.getPointAtLength(Math.min(waveLen, waveLen * arrowT + eps));
        const behind = path.getPointAtLength(Math.max(0, waveLen * arrowT - eps));
        ax = pt.x;
        ay = pt.y;
        angle = (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI;
      }
      if (arrowRef.current) {
        arrowRef.current.setAttribute(
          "transform",
          `translate(${ax} ${ay}) rotate(${angle})`
        );
        // Fade the arrow out as it nears step 1 so it doesn't stick on the
        // closed loop next to the video.
        const arrOp = mArc < 0.92 ? 1 : 1 - (mArc - 0.92) / 0.08;
        arrowRef.current.style.opacity = String(Math.max(0, arrOp));
      }

      // ---------- Nodes (positions + trail / active appearance) ----------
      // During travel, timeline idx is -1; still colour every node the arrow
      // has passed (mint) using path progress, not only i < active.
      const nodeIdx =
        mobileTl &&
        (mobilePaintArrowTraveling ||
          (!journeyPinned && mobAllowHeroExitRef.current))
          ? -1
          : idx;
      paintJourneyNodes({
        tl: mobileTl
          ? { arrow: arrowDraw, idx: nodeIdx }
          : deskTimeline,
        m,
        mWave,
        waveNodes,
        circleTopNodes,
        geom,
        nodeRefs,
        numRefs,
        stepCount: STEP_COUNT,
        pathDiv: PATH_DIV,
        storyStep: mobileTl ? mobilePaintStoryStep : -1,
        arrowTraveling: mobileTl && mobilePaintArrowTraveling,
      });

      // Compute the SVG's actual pixel rect. Using getBoundingClientRect
      // lets us correctly align with whatever sizing CSS applies — on
      // mobile the SVG is stretched to fill width (height: auto), while
      // on desktop `preserveAspectRatio="xMidYMid meet"` fits by width.
      const cw = canvas.clientWidth;
      const ch = canvas.clientHeight;
      const svgEl = svgRef.current;
      let svgPxW;
      let svgPxH;
      let svgX;
      let svgY;
      if (svgEl) {
        const canvasRect = canvas.getBoundingClientRect();
        const svgRect = svgEl.getBoundingClientRect();
        svgPxW = svgRect.width;
        svgPxH = svgRect.height;
        svgX = svgRect.left - canvasRect.left;
        svgY = svgRect.top - canvasRect.top;
      } else {
        const sx = cw / geom.VIEW_W;
        const sy = ch / geom.VIEW_H;
        const fallbackScale = Math.min(sx, sy);
        svgPxW = geom.VIEW_W * fallbackScale;
        svgPxH = geom.VIEW_H * fallbackScale;
        svgX = (cw - svgPxW) / 2;
        svgY = (ch - svgPxH) / 2;
      }
      const svgScale = svgPxW / geom.VIEW_W;

      // ---------- Pixel-align the HTML finale circle to the SVG circle ----
      if (finaleRef.current) {
        const pxCx = svgX + geom.CIRCLE_CX * svgScale;
        const pxCy = svgY + geom.CIRCLE_CY * svgScale;
        const pxR = geom.CIRCLE_R * svgScale;
        finaleRef.current.style.setProperty("--sp-finale-cx", `${pxCx}px`);
        finaleRef.current.style.setProperty("--sp-finale-cy", `${pxCy}px`);
        finaleRef.current.style.setProperty("--sp-finale-r", `${pxR}px`);
      }

      // ---------- SVG outer opacity (slight dim during finale_hold) ----------
      if (svgRef.current) {
        const svgOp = 1 - 0.35 * f;
        svgRef.current.style.opacity = String(svgOp);
      }

      // ---------- Active step + per-step video (mobile: scroll-driven expand/collapse) ----------
      let panelIdx = idx;
      let panelScale = s;

      const mobileStoryPanel =
        mobileTl &&
        secRect &&
        journeyInView &&
        mobDisplayStepRef.current >= 0 &&
        (secRect.top <= MOBILE_JOURNEY_LEAVE_TOP_PX || !mobAllowHeroExitRef.current);

      if (mobileTl) {
        if (!mobileStoryPanel) {
          panelIdx = -1;
          panelScale = 0;
        } else {
          panelIdx = idx;
          panelScale = s;
        }
      }

      if (idx !== lastActive) {
        lastActive = idx;
        setActive(mobileTl && !mobileStoryPanel ? -1 : idx);
      }

      const videoIdx =
        panelIdx >= 0 && panelScale >= (mobileTl ? 0.82 : 0.999)
          ? panelIdx
          : -1;
      if (videoIdx !== lastVideo) {
        lastVideo = videoIdx;
        setVideoActive(videoIdx);
      }

      // ---------- Finale overlay (video + capsules) ----------
      // Desktop: video appears once the closing arc is mostly drawn.
      // Mobile: scale tracks morph (0→1) so the video disc fills the SVG circle
      // perimeter when the loop closes; finale hold keeps it at 1.
      const overlay = mobileTl
        ? clamp01(Math.max(m, f))
        : Math.max(Math.max(0, (mArc - 0.6) / 0.4), f);
      // Mobile: finer quantisation (160 steps) for smoother overlay blend. Desktop: HEAD (100 steps).
      const oqSteps = mobileTl ? 160 : 100;
      const oq = Math.round(overlay * oqSteps) / oqSteps;
      if (oq !== lastFinale) {
        lastFinale = oq;
        setFinaleProgress(oq);
      }

      // ---------- Per-step zoom card ----------
      scaleSmooth = s;
      if (panelIdx >= 0) {
        lastNodePt = path.getPointAtLength(waveLen * (panelIdx / PATH_DIV));
      } else if (idx >= 0) {
        lastNodePt = path.getPointAtLength(waveLen * (idx / PATH_DIV));
      }

      if (panelRef.current) {
        const cardScale = scaleSmooth;
        if (cardScale > 0.003 && lastNodePt) {
          const np = lastNodePt;
          // Convert node viewBox coords → canvas pixel coords using the
          // SVG's actual scale + offset (works for both orientations).
          const nx = svgX + np.x * svgScale;
          const ny = svgY + np.y * svgScale;
          const PW = panelRef.current.offsetWidth;
          const PH = panelRef.current.offsetHeight;
          const sideX = np.x < geom.VIEW_W / 2 ? 1 : -1;
          const sideY = np.y < geom.VIEW_H / 2 ? 1 : -1;
          const maxOffX = Math.max(0, (cw - FULL_SCALE * PW) / 2 - 16);
          const maxOffY = Math.max(0, (ch - FULL_SCALE * PH) / 2 - 16);
          const offX = sideX * Math.min(cw * 0.10, maxOffX);
          const offY = sideY * Math.min(40, maxOffY);
          let cx2 = cw / 2 + offX;
          let cy2 = ch / 2 + offY;
          // Mobile (incl. ~393×852): expand every step toward the viewport center,
          // not-quadrant nudges — convert visual center to canvas-local coords.
          if (geom.VIEW_W === MOBILE_GEOM.VIEW_W) {
            const canvasRect = canvas.getBoundingClientRect();
            cx2 = window.innerWidth / 2 - canvasRect.left;
            cy2 = window.innerHeight / 2 - canvasRect.top;
            const pad = 12;
            const halfScaledW = (FULL_SCALE * PW) / 2;
            const halfScaledH = (FULL_SCALE * PH) / 2;
            cx2 = Math.min(
              cw - pad - halfScaledW,
              Math.max(pad + halfScaledW, cx2),
            );
            cy2 = Math.min(
              ch - pad - halfScaledH,
              Math.max(pad + halfScaledH, cy2),
            );
          }
          const centerX = nx + cardScale * (cx2 - nx);
          const centerY = ny + cardScale * (cy2 - ny);
          const dScale = cardScale * FULL_SCALE;
          const left = centerX - (dScale * PW) / 2;
          const top = centerY - (dScale * PH) / 2;
          panelRef.current.style.transform = `translate(${left}px, ${top}px) scale(${dScale})`;
          // Desktop: restore original opacity formula (s * 1.2 → faster fade-in).
          // Mobile: sine easing on the smoothed scale for a gentler appearance.
          panelRef.current.style.opacity = String(
            mobileTl
              ? Math.min(1, cardScale)
              : Math.min(1, cardScale * 1.2),
          );
          panelRef.current.style.pointerEvents = cardScale > 0.7 ? "auto" : "none";
        } else {
          panelRef.current.style.opacity = "0";
          panelRef.current.style.transform = `translate(0px, 0px) scale(0)`;
          panelRef.current.style.pointerEvents = "none";
        }
      }
    };

    const mobileStory = geom.VIEW_W === MOBILE_GEOM.VIEW_W;

    const loop = () => {
      rafId = requestAnimationFrame(loop);

      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTimeRef.current) / 1000);
      lastTimeRef.current = now;

      const rect = sec.getBoundingClientRect();
      const total = sec.offsetHeight - window.innerHeight;
      const rawP = Math.max(0, Math.min(1, -rect.top / Math.max(total, 1)));
      let targetP = rawP;

      if (mobileStory) {
        targetP = rect.top > 0 ? 0 : rawP;
        smoothP = targetP;
      } else {
        smoothP += (targetP - smoothP) * JOURNEY_SCROLL_BLEND_DESKTOP;
      }

      if (rect.top > window.innerHeight + 50 || rect.bottom < -50) {
        if (mobileStory) setJourneyMobileScrollGate(false);
        return;
      }
      update(smoothP, dt);
    };

    rafId = requestAnimationFrame(loop);

    const onResize = () => {
      const rect = sec.getBoundingClientRect();
      const total = sec.offsetHeight - window.innerHeight;
      const rawP = Math.max(0, Math.min(1, -rect.top / Math.max(total, 1)));
      const p = mobileStory ? (rect.top > 0 ? 0 : rawP) : rawP;
      update(p, 0);
    };
    window.addEventListener("resize", onResize);
    return () => {
      setJourneyMobileScrollGate(false);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(rafId);
    };
  }, [waveSamples, circleTopSamples, geom]);

  useLayoutEffect(() => {
    const sec = sectionRef.current;
    if (!sec || !waveSamples || !nodeRefs.current[0]) return;

    if (isMobile) {
      const m = mobMorphRef.current;
      const mWave = m === 0 ? 0 : Math.min(1, m / MORPH_WAVE_FRAC);
      paintJourneyNodes({
        tl: {
          arrow: mobArrowRef.current,
          idx: mobActiveCardRef.current,
          scale: mobCardScaleRef.current,
        },
        m,
        mWave,
        waveNodes,
        circleTopNodes,
        geom,
        nodeRefs,
        numRefs,
        stepCount: STEP_COUNT,
        pathDiv: PATH_DIV,
        storyStep: mobDisplayStepRef.current,
        arrowTraveling: false,
      });
    } else {
      const total = sec.offsetHeight - window.innerHeight;
      const rect = sec.getBoundingClientRect();
      const rawP = Math.max(0, Math.min(1, -rect.top / Math.max(total, 1)));
      const tl = timeline(rawP, false);
      const m = tl.morph;
      const mWave = m === 0 ? 0 : Math.min(1, m / MORPH_WAVE_FRAC);
      paintJourneyNodes({
        tl,
        m,
        mWave,
        waveNodes,
        circleTopNodes,
        geom,
        nodeRefs,
        numRefs,
        stepCount: STEP_COUNT,
        pathDiv: PATH_DIV,
      });
    }
  }, [
    active,
    isMobile,
    videoActive,
    finaleProgress,
    waveSamples,
    circleTopSamples,
    geom,
  ]);

  // Play only the video for the currently fully-zoomed step.
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === videoActive) {
        try {
          v.currentTime = 0;
          v.playbackRate = 1.5;
          const r = v.play();
          if (r && typeof r.catch === "function") r.catch(() => {});
        } catch (e) {
          /* noop */
        }
      } else {
        try {
          v.pause();
        } catch (e) {
          /* noop */
        }
      }
    });
  }, [videoActive]);

  // Auto-play the active step's video when it reaches full zoom (gated by
  // videoActive). The non-active videos are paused to save CPU.

  return (
    <section
      ref={sectionRef}
      id="journey"
      className={`sp-journey ${isMobile ? "is-mobile" : ""}`}
      {...(isMobile ? { "data-sp-snap-section": "" } : {})}
      data-testid="journey-section"
    >
      <div className="sp-journey-inner">
        <div className="sp-journey-halo" aria-hidden="true" />

        <div className="sp-journey-head" data-testid="journey-header">
          <h2 className="sp-journey-title">
            A learning journey that{" "}
            <span className="sp-grad-word">keeps getting smarter</span>
          </h2>
          <p className="sp-journey-sub">
            From the first learning plan to every class, test, and AI tutor
            session — Sparkl keeps adapting around the student.
          </p>
        </div>

        <div
          ref={canvasRef}
          className="sp-journey-canvas"
          data-testid="journey-canvas"
        >
          <svg
            ref={svgRef}
            className="sp-journey-svg"
            viewBox={`0 0 ${geom.VIEW_W} ${geom.VIEW_H}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            style={{ transition: "opacity 120ms linear" }}
          >
            <defs>
              <linearGradient id="sp-active-grad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#97D8C4" />
                <stop offset="100%" stopColor="#FAAC00" />
              </linearGradient>
            </defs>
            <path
              ref={pathRef}
              d={geom.CURVE_D}
              fill="none"
              stroke="rgba(30,36,48,0.10)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              ref={activeStrokeRef}
              d={geom.CURVE_D}
              fill="none"
              stroke="url(#sp-active-grad)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={1}
              strokeDashoffset={1}
              style={{ transition: "stroke-dashoffset 80ms linear" }}
            />
            <path
              ref={closingArcRef}
              d={geom.CLOSING_ARC_D}
              fill="none"
              stroke="url(#sp-active-grad)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
              {STEPS.map((step, i) => (
                <g
                  key={step.id}
                  ref={(el) => (nodeRefs.current[i] = el)}
                  data-testid={`journey-node-${i + 1}`}
                >
                  {/*
                    Fill / radius / filter are driven every frame in the scroll
                    update() so trail dots stay mint during travel (idx === -1).
                  */}
                  <circle
                    r={isMobile ? 14 : 10}
                    fill="#FFFFFF"
                    stroke="#FFFFFF"
                    strokeWidth={2}
                    style={{
                      filter:
                        "drop-shadow(0 6px 14px rgba(31,41,55,0.10))",
                      transition:
                        "r 0.45s cubic-bezier(0.22,1,0.36,1), fill 0.5s ease",
                    }}
                  />
                  <text
                    ref={(el) => (numRefs.current[i] = el)}
                    x={0}
                    y={isMobile ? -36 : -26}
                    textAnchor="middle"
                    fontFamily="Manrope, sans-serif"
                    fontWeight="700"
                    fontSize={isMobile ? "17" : "11"}
                    letterSpacing="0.14em"
                    fill="rgba(30,36,48,0.55)"
                    style={{ transition: "fill 0.4s ease" }}
                  >
                    {step.num}
                  </text>
                </g>
              ))}
              <g ref={arrowRef} style={{ pointerEvents: "none" }}>
                <image
                  href={ARROW_URL}
                  xlinkHref={ARROW_URL}
                  x={-30}
                  y={-30}
                  width={60}
                  height={60}
                  style={{
                    filter: "drop-shadow(0 10px 18px rgba(31,41,55,0.18))",
                  }}
                />
              </g>
            </svg>

            <div
              ref={panelRef}
              className="sp-zoom"
              data-testid="journey-zoom-panel"
              style={{
                transform: "translate(0,0) scale(0)",
                opacity: 0,
              }}
            >
              <div className="sp-zoom-media">
                {STEPS.map((s, i) => (
                  <video
                    key={s.id}
                    ref={(el) => (videoRefs.current[i] = el)}
                    src={s.videoPath ? getAssetUrl(s.videoPath) : s.video}
                    muted
                    loop
                    playsInline
                    preload={i === 0 ? "auto" : "metadata"}
                    webkit-playsinline="true"
                    x5-playsinline="true"
                    className={`sp-zoom-video ${
                      i === active ? "is-active" : ""
                    }`}
                  />
                ))}
              </div>
              <div className="sp-zoom-text">
                <span className="sp-zoom-num">
                  Step {active >= 0 ? STEPS[active].num : "01"}
                </span>
                <h3 className="sp-zoom-title">
                  {active >= 0 ? STEPS[active].title : ""}
                </h3>
                <p className="sp-zoom-body">
                  {active >= 0 ? STEPS[active].body : ""}
                </p>
              </div>
            </div>

            <JourneyFinale s={finaleProgress} finaleRef={finaleRef} isMobile={isMobile} />
          </div>
      </div>
    </section>
  );
}
